import { httpModifierAdapter } from './Modifiers.http';

// Operations still served by the mock. To migrate one call to its live endpoint once the
// backend ships it, delete that line: the operation then falls through to
// httpModifierAdapter while everything else keeps mocking.
const MOCKED_OPERATIONS = [
  'createModifierGroup',
  'updateModifierGroup',
  'getModifierGroups',
  'createModifier',
  'updateModifier',
  'linkModifierGroupToItems',
  'unlinkModifierGroupFromItem',
];

// Deploy environments that get the mock, named explicitly: anything else, including an
// unrecognised or missing value, talks to the real endpoints. Serving a partner fabricated
// menu data is far worse than an internal tester seeing a failed request, so an environment
// this list does not know about must never fall into the mock branch. Staging is deliberately
// absent; it runs against real backends.
//
// The value is the deploy environment the server injects as window.ENV (config/*.json).
// NODE_ENV cannot stand in for it: every deployed image bakes NODE_ENV to "production", so it
// reads the same on dev-studios as it does in front of partners. Read per call so the gateway
// does not depend on whether window.ENV is assigned before this module first evaluates.
const MOCKED_ENVIRONMENTS = ['development', 'local', 'local-e2e'];
const servesMockData = () => typeof window !== 'undefined' && MOCKED_ENVIRONMENTS.includes(window.ENV);

// Its own chunk, so the mock and its Faker fixtures stay out of the bundles partners
// download; production never resolves this import. A failed fetch drops the cached promise,
// otherwise one offline moment would leave every modifier operation rejecting for the rest
// of the session with no way back.
let mockAdapterPromise;
const loadMockAdapter = () => {
  if (!mockAdapterPromise) {
    mockAdapterPromise = import(/* webpackChunkName: "modifiers-mock" */ './Modifiers.mock')
      .then(mockModule => mockModule.mockModifierAdapter)
      .catch(error => {
        mockAdapterPromise = undefined;
        throw error;
      });
  }

  return mockAdapterPromise;
};

const modifierGateway = MOCKED_OPERATIONS.reduce(
  (gateway, operation) => ({
    ...gateway,
    [operation]: async (...args) => {
      if (!servesMockData()) {
        return httpModifierAdapter[operation](...args);
      }

      const mockAdapter = await loadMockAdapter();
      return mockAdapter[operation](...args);
    },
  }),
  { ...httpModifierAdapter }
);

export default modifierGateway;
